import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getPatientAppointments } from './api';
import LoadingSpinner from './LoadingSpinner';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Receipt from './Receipt'; // <-- ADDED
import './Appointmenthistory.css';

// --- SVG Icons ---
const CalendarIcon = () => <svg xmlns="http://www.w.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const RupeeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-5 4h5m2 5H7a2 2 0 01-2-2V7a2 2 0 012-2h5l2 2h2a2 2 0 012 2v4a2 2 0 01-2 2z" /></svg>;
const LogoutIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;
const ReceiptIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const InfoIcon = () => <svg xmlns="http://www.w.org/2000/svg" className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;


const PaymentHistory = () => {
    const { patientId, isAuthenticated, loading: authLoading, logout } = useAuth();
    const [allAppointments, setAllAppointments] = useState([]);
    const [patientInfo, setPatientInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    
    // --- NEW: State and Ref for downloading ---
    const [isDownloading, setIsDownloading] = useState(false);
    const [receiptDataForDownload, setReceiptDataForDownload] = useState(null);
    const receiptComponentRef = useRef();

    // Fetches appointment data
    useEffect(() => {
        if (!authLoading && isAuthenticated && patientId) {
            setLoading(true);
            getPatientAppointments(patientId)
                .then(response => {
                    if (response.data.success) {
                        setAllAppointments(response.data.appointmentHistory);
                        setPatientInfo(response.data.patient);
                    } else {
                        setError(response.data.message || 'Failed to fetch payment history.');
                    }
                })
                .catch(err => setError(err.response?.data?.message || 'An error occurred while fetching history.'))
                .finally(() => setLoading(false));
        } else if (!authLoading && !isAuthenticated) {
            setLoading(false);
        }
    }, [patientId, isAuthenticated, authLoading]);

    // --- NEW: useEffect to generate and download the PDF ---
    useEffect(() => {
        if (receiptDataForDownload && receiptComponentRef.current) {
            html2canvas(receiptComponentRef.current, { scale: 2, useCORS: true })
            .then(canvas => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
                
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                
                const isSuccess = receiptDataForDownload.paymentStatus === 'paid';
                const status = isSuccess ? 'Success' : 'Failed';
                const fileName = `Receipt_${status}_${receiptDataForDownload.razorpayOrderId?.slice(-6) || receiptDataForDownload._id.slice(-6)}.pdf`;
                
                pdf.save(fileName);
            })
            .catch(err => {
                console.error("Error generating PDF:", err);
                alert("Could not generate the receipt PDF. Please try again.");
            })
            .finally(() => {
                // Reset state to hide the component and re-enable buttons
                setReceiptDataForDownload(null);
                setIsDownloading(false);
            });
        }
    }, [receiptDataForDownload]);


    // Helper functions for filtering and display
    const { successfulPayments, failedOrPendingPayments } = useMemo(() => {
        const successful = allAppointments.filter(appt => appt.paymentStatus === 'paid');
        const failedOrPending = allAppointments.filter(appt => appt.paymentStatus !== 'paid');
        return { successfulPayments: successful, failedOrPendingPayments: failedOrPending };
    }, [allAppointments]);

    const getPaymentStatusInfo = (status) => {
        switch (status) {
            case 'paid': return { text: 'Paid', className: 'status-paid' };
            case 'failed': return { text: 'Failed', className: 'status-failed' };
            case 'pending': return { text: 'Pending', className: 'status-pending' };
            default: return { text: (status || 'Unknown').replace(/_/g, ' '), className: 'status-default' };
        }
    };
    
    const getVisibleAppointments = () => {
        switch(activeTab) {
            case 'successful': return successfulPayments;
            case 'failed_pending': return failedOrPendingPayments;
            default: return allAppointments;
        }
    }
    
    // --- NEW: Handler to start the download process ---
    const handleDownloadReceipt = (appointmentDetails) => {
        if (isDownloading) return; // Prevent multiple clicks
        setIsDownloading(true);
        setReceiptDataForDownload(appointmentDetails);
    };

    // Conditional rendering for loading/auth states
    if (authLoading) return <div className="loading-container"><LoadingSpinner /></div>;
    if (!isAuthenticated) return <div className="error-container"><h3>Please Log In</h3><p>You must be logged in to view your payment history.</p></div>;
    if (error) return <div className="error-container"><h3>Error: {error}</h3><button onClick={logout} className="logout-button">Log Out</button></div>;
    // --- STEP 5: The main return statement. ---
    return (
         <>
            <Navbar />
            <div className="payment-history-page">
                <div className="history-header-glass">
                    <div className="header-content">
                        <div>
                            <h2>Payment History</h2>
                            <p>Review your transactions and download receipts for your consultations.</p>
                        </div>
                        <button onClick={logout} className="logout-button-icon" title="Logout">
                            <LogoutIcon /> <span>Logout</span>
                        </button>
                    </div>
                </div>

                <div className="history-container">
                    <div className="tabs">
                        <button className={`tab-button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
                            All Payments ({allAppointments.length})
                        </button>
                        <button className={`tab-button ${activeTab === 'successful' ? 'active' : ''}`} onClick={() => setActiveTab('successful')}>
                            Successful ({successfulPayments.length})
                        </button>
                        <button className={`tab-button ${activeTab === 'failed_pending' ? 'active' : ''}`} onClick={() => setActiveTab('failed_pending')}>
                            Failed / Pending ({failedOrPendingPayments.length})
                        </button>
                    </div>

                    <div className="tab-content">
                        {loading ? <LoadingSpinner /> : (
                            // Render the list of appointments
                            getVisibleAppointments().length === 0 ? (
                                <div className="no-records-card fade-in">
                                    <ReceiptIcon />
                                    <h3>No Transactions Found</h3>
                                    <p>There are no transactions in this category.</p>
                                </div>
                            ) : (
                                <div className="payments-grid">
                                    {getVisibleAppointments().map((appt, index) => {
                                        const statusInfo = getPaymentStatusInfo(appt.paymentStatus);
                                        const canDownloadReceipt = appt.paymentStatus === 'paid' || appt.paymentStatus === 'failed';
                                        
                                        return (
                                            <div key={appt._id} className="payment-card fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                                                <div className="card-header">
                                                    <div className="doctor-info">
                                                        <h3> {appt.doctorDetails?.name || 'N/A'}</h3>
                                                        <p className="doctor-speciality">{appt.doctorDetails?.speciality || 'Speciality'}</p>
                                                    </div>
                                                    <span className={`status-badge ${statusInfo.className}`}>{statusInfo.text}</span>
                                                </div>
                                                <div className="card-body">
                                                    <div className="appointment-details">
                                                        <p><CalendarIcon /> <span>{new Date(appt.appointmentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span></p>
                                                        <p><ClockIcon /> <span>{appt.appointmentTime}</span></p>
                                                    </div>
                                                    <div className="payment-amount">
                                                        <RupeeIcon />
                                                        <span>{appt.doctorFee.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                <div className="transaction-details">
                                                    <h4><InfoIcon /> Transaction Details</h4>
                                                    <p><strong>Appointment No:</strong> {appt.appointmentNumber || 'N/A'}</p>
                                                    <p><strong>Payment ID:</strong> {appt.razorpayPaymentId || 'N/A'}</p>
                                                    <p><strong>Order ID:</strong> {appt.razorpayOrderId || 'N/A'}</p>
                                                </div>
                                                <div className="card-footer">
                                                    <small>Booked on: {new Date(appt.createdAt).toLocaleDateString()}</small>
                                                    {canDownloadReceipt && (
                                                        <button 
                                                            className="receipt-button" 
                                                            onClick={() => handleDownloadReceipt(appt)}
                                                            disabled={isDownloading}
                                                        >
                                                            {isDownloading && receiptDataForDownload?._id === appt._id ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    <span style={{ marginLeft: '8px' }}>Generating...</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ReceiptIcon /> Download Receipt
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </div>
                
                {/* Hidden component for PDF generation, placed outside the main flow */}
                <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }}>
                    <Receipt 
                        ref={receiptComponentRef} 
                        appointment={receiptDataForDownload}
                        patient={patientInfo} 
                    />
                </div>
            </div>
            <Footer/>
         </>
    );
};

export default PaymentHistory;